// ---------------------------------------------------------------------------
// wizard-state.tsx — React context + reducer for /studio/new wizard.
// In-memory only (no URL persistence for MVP). All step transitions validated
// by guards — Steps cannot advance unless required data is captured.
// ---------------------------------------------------------------------------

import React, { createContext, useContext, useReducer } from 'react'
import {
  appTargetFromRuntimeTargets,
  runtimeTargetsForAppTarget,
  type StudioAppClass,
  type StudioDesignSystemReleaseIdentity,
  type StudioRuntimeTarget,
  type StudioSecurityProfile,
} from '../lib/studio-project-plan'
import type { DesignSystemState } from '@/features/design-system/lib/design-system-options'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProjectSource = 'template' | 'scratch'
export type TargetPlatform = 'web' | 'mobile' | 'both'

export interface WizardState {
  /** 1-indexed step number (1..4). Step 2 content depends on source. */
  step: 1 | 2 | 3 | 4
  source: ProjectSource | null

  // Step 2-template
  selectedTemplateId: string | null
  selectedTemplateName: string | null

  // Step 2-scratch
  selectedPresetId: string | null
  selectedPresetName: string | null
  selectedPresetTokens: DesignSystemState | null
  selectedDesignSystemRelease: StudioDesignSystemReleaseIdentity | null
  selectedDesignSystemReleaseName: string | null

  // Step 3
  startPrompt: string
  starterPathId: string | null
  starterPathName: string | null
  projectName: string
  projectDescription: string
  targetPlatform: TargetPlatform
  appClass: StudioAppClass
  runtimeTargets: StudioRuntimeTarget[]
  securityProfile: StudioSecurityProfile

  // Step 4 runtime
  isCreating: boolean
  createError: string | null
}

export const INITIAL_WIZARD_STATE: WizardState = {
  step: 1,
  source: null,
  selectedTemplateId: null,
  selectedTemplateName: null,
  selectedPresetId: null,
  selectedPresetName: null,
  selectedPresetTokens: null,
  selectedDesignSystemRelease: null,
  selectedDesignSystemReleaseName: null,
  startPrompt: '',
  starterPathId: null,
  starterPathName: null,
  projectName: '',
  projectDescription: '',
  targetPlatform: 'web',
  appClass: 'static',
  runtimeTargets: ['web'],
  securityProfile: 'standard',
  isCreating: false,
  createError: null,
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type WizardAction =
  | { type: 'SELECT_SOURCE'; source: ProjectSource }
  | {
      type: 'APPLY_STARTER_PATH'
      id: string
      name: string
      source: ProjectSource
      prompt: string
      fallbackName: string
      fallbackDescription: string
      appClass: StudioAppClass
      runtimeTargets: StudioRuntimeTarget[]
      securityProfile: StudioSecurityProfile
    }
  | { type: 'SET_START_PROMPT'; prompt: string }
  | { type: 'SELECT_TEMPLATE'; id: string; name: string }
  | { type: 'SELECT_PRESET'; id: string; name: string; tokens?: DesignSystemState }
  | {
      type: 'SELECT_DESIGN_SYSTEM_RELEASE'
      release: StudioDesignSystemReleaseIdentity
      name: string
    }
  | { type: 'SET_PROJECT_NAME'; name: string }
  | { type: 'SET_PROJECT_DESCRIPTION'; description: string }
  | { type: 'SET_TARGET_PLATFORM'; platform: TargetPlatform }
  | { type: 'SET_APP_CLASS'; appClass: StudioAppClass }
  | { type: 'TOGGLE_RUNTIME_TARGET'; target: StudioRuntimeTarget }
  | { type: 'SET_SECURITY_PROFILE'; profile: StudioSecurityProfile }
  | { type: 'NEXT' }
  | { type: 'BACK' }
  | { type: 'SET_CREATING'; isCreating: boolean }
  | { type: 'SET_CREATE_ERROR'; error: string | null }
  | { type: 'RESET' }

// ---------------------------------------------------------------------------
// Guard: can we advance from the current step?
// ---------------------------------------------------------------------------

export function canAdvance(state: WizardState): boolean {
  switch (state.step) {
    case 1:
      return state.source !== null
    case 2:
      if (state.source === 'template') return state.selectedTemplateId !== null
      if (state.source === 'scratch') {
        if (state.selectedDesignSystemRelease !== null) return true
        if (state.selectedPresetId === null) return false
        return state.starterPathId === null || state.selectedPresetTokens !== null
      }
      return false
    case 3:
      return state.projectName.trim().length > 0
    case 4:
      return false // Step 4 uses "Create" button, not Next
    default:
      return false
  }
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SELECT_SOURCE':
      return { ...state, source: action.source }
    case 'SET_START_PROMPT':
      return { ...state, startPrompt: action.prompt }
    case 'APPLY_STARTER_PATH': {
      const prompt = action.prompt.trim()
      return {
        ...state,
        source: action.source,
        startPrompt: action.prompt,
        starterPathId: action.id,
        starterPathName: action.name,
        projectName: state.projectName.trim() ? state.projectName : action.fallbackName,
        projectDescription: prompt || state.projectDescription || action.fallbackDescription,
        appClass: action.appClass,
        runtimeTargets: action.runtimeTargets,
        targetPlatform: appTargetFromRuntimeTargets(action.runtimeTargets),
        securityProfile: action.securityProfile,
      }
    }
    case 'SELECT_TEMPLATE':
      return {
        ...state,
        selectedTemplateId: action.id,
        selectedTemplateName: action.name,
      }
    case 'SELECT_PRESET':
      return {
        ...state,
        selectedPresetId: action.id,
        selectedPresetName: action.name,
        selectedPresetTokens: action.tokens ?? null,
        selectedDesignSystemRelease: null,
        selectedDesignSystemReleaseName: null,
      }
    case 'SELECT_DESIGN_SYSTEM_RELEASE':
      return {
        ...state,
        selectedPresetId: null,
        selectedPresetName: null,
        selectedPresetTokens: null,
        selectedDesignSystemRelease: action.release,
        selectedDesignSystemReleaseName: action.name,
      }
    case 'SET_PROJECT_NAME':
      return { ...state, projectName: action.name }
    case 'SET_PROJECT_DESCRIPTION':
      return { ...state, projectDescription: action.description }
    case 'SET_TARGET_PLATFORM':
      return {
        ...state,
        targetPlatform: action.platform,
        runtimeTargets: runtimeTargetsForAppTarget(action.platform),
      }
    case 'SET_APP_CLASS':
      return { ...state, appClass: action.appClass }
    case 'TOGGLE_RUNTIME_TARGET': {
      const nextTargets = state.runtimeTargets.includes(action.target)
        ? state.runtimeTargets.filter((target) => target !== action.target)
        : [...state.runtimeTargets, action.target]
      const runtimeTargets = nextTargets.length > 0 ? nextTargets : [action.target]
      return {
        ...state,
        runtimeTargets,
        targetPlatform: appTargetFromRuntimeTargets(runtimeTargets),
      }
    }
    case 'SET_SECURITY_PROFILE':
      return { ...state, securityProfile: action.profile }
    case 'NEXT': {
      if (!canAdvance(state)) return state
      const next = (state.step + 1) as WizardState['step']
      return { ...state, step: next }
    }
    case 'BACK': {
      if (state.step <= 1) return state
      const prev = (state.step - 1) as WizardState['step']
      return { ...state, step: prev, createError: null }
    }
    case 'SET_CREATING':
      return { ...state, isCreating: action.isCreating }
    case 'SET_CREATE_ERROR':
      return { ...state, createError: action.error }
    case 'RESET':
      return INITIAL_WIZARD_STATE
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface WizardContextValue {
  state: WizardState
  dispatch: React.Dispatch<WizardAction>
}

const WizardContext = createContext<WizardContextValue | null>(null)

export function WizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_WIZARD_STATE)
  return (
    <WizardContext.Provider value={{ state, dispatch }}>
      {children}
    </WizardContext.Provider>
  )
}

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext)
  if (!ctx) throw new Error('useWizard must be used inside <WizardProvider>')
  return ctx
}
